import React from 'react';
import LegalPage, { Section, List, Link } from '@/components/Pages/LegalPage';
import Paragraph from '@/components/Typography/Paragraph';
import Text from '@/components/Typography/Text';
import MetaHead from '@/components/MetaHead';

const TermsOfSalePage = () => {
  return (
    <LegalPage
      title="Terms of Sale"
      lastUpdated="September 6, 2026"
    >
      <MetaHead 
        title="Terms of Sale"
        description="Gab AI Terms of Sale. Subscription billing, payment methods, refund policy, and cancellation terms for Gab AI Plus and credits."
        canonical="/terms-of-sale"
        keywords="Gab AI terms of sale, subscription terms, payment terms, refund policy, billing"
      />

      <Paragraph>
        Gab AI Inc ("We" or "Gab") hereby allow you to access certain premium features or content in exchange for a one-time or recurring fee, as applicable to the relevant features or content, such as our Gab AI Plus service (each a "Paid Service" and collectively the "Paid Services"). Your transactions and any other use of the Paid Services are subject to these Gab Paid Service Terms of Sale ("Terms").
      </Paragraph>

      <Section title="1. Your Acceptance">
        <Paragraph>
          By using a Paid Service, you signify your agreement to (1) these Terms of Sale; and (2) Gab AI Inc's <Link href="/terms-of-service">Terms of Service</Link>, <Link href="/privacy-policy">Privacy Policy</Link>, and all other terms and conditions that generally apply to the Website (as such term is defined in the Terms of Service). Please read the Terms carefully. If you do not understand the Terms, or do not accept any part of them, then you may not use the Paid Services.
        </Paragraph>
        <Paragraph>
          Each time you place an order for a Paid Service (including when you order individual subscriptions or items of content), you enter into a separate contract for services on these Terms. When you complete a purchase, you signify that you wish to enter into a binding contract for the provision of the applicable Paid Services.
        </Paragraph>
      </Section>

      <Section title="2. Payment, Refund and Cancellation Policy">
        <Paragraph>
          Gab accepts payment via the current payment method indicated prior to purchase, which may include Bitcoin and/or any other form of payment that we make available to you from time to time. You must tender valid payment or have a valid accepted form of payment on file in order to purchase Paid Services. You agree to abide by any relevant Terms of Service or other legal agreement, whether with Gab or a third party, that governs your use of a given payment processing method. Prices are disclosed before purchase. Subscription price increases require at least 30 days' notice before your next renewal. Auto Top-Up purchases remain subject to the specific price you authorize and the review requirements in section 3.
        </Paragraph>
        <Paragraph>
          Billing periods may be monthly or annually. The renewal period applicable to any subscription will be the same as the initial billing period for the Paid Services.
        </Paragraph>
        <Paragraph>
          If Gab is required to collect or pay any taxes in connection with your purchase of a Paid Service, such taxes will be charged to you at the time of each purchase transaction. Additionally, if required by law, you are responsible for reporting and paying certain taxes in connection with your purchase and use of a Paid Service. Such taxes may include duties, customs fees, or other taxes (other than income tax), along with any related penalties or interest, as applicable to your purchase or use of Paid Services made through Gab.
        </Paragraph>

        <Section title="REFUNDS">
          <Paragraph>
            Subscription and credit purchases are generally final and non-refundable, except where required by law or as provided in our <Link href="/refund-policy">Refund Policy</Link>. Nothing in these Terms waives rights relating to unauthorized, duplicate, or incorrect charges.
          </Paragraph>
        </Section>

        <Section title="SUBSCRIPTION CANCELLATIONS">
          <Paragraph>
            If you purchase a subscription to a Paid Service that automatically renews, you may cancel the subscription any time before the end of the current billing period and the cancellation will take effect on the next billing period. You will retain access to the Paid Service from the time you cancel until the start of the next billing period, and generally will not receive a refund or credit for any remaining days in your current billing period, except where required by law or provided in our Refund Policy. If you have any questions about cancelling your subscription, please contact us at ai@gab.com.
          </Paragraph>
        </Section>
      </Section>

      <Section title="3. Credit Packs and Auto Top-Up Service">
        <Section title="CREDIT PACKS">
          <Paragraph>
            Credit packs are prepaid access to eligible Gab AI features, including Work, beyond any included
            allowance. The pack price, currency, and number of credits are displayed before purchase. A one-time pack
            purchase does not enroll you in Auto Top-Up. Purchased credits do not expire and remain available until
            used, subject to account restrictions or termination under the Terms of Service and applicable law.
            Subscription allowances and promotional credits may have separately disclosed conditions.
          </Paragraph>
          <Paragraph>
            Credits may be used only within eligible Gab AI services. They are not money, a bank deposit, or an
            investment; they cannot be transferred, resold, or exchanged for cash, except where required by law.
            Credit purchases are generally non-refundable, subject to our Refund Policy and mandatory legal rights.
            Canceling a subscription or disabling Auto Top-Up does not by itself expire purchased credits.
          </Paragraph>
          <Paragraph>
            The credits required for a feature or task depend on the models, tools, processing, and pricing
            applicable to that use. A Work task may involve multiple billable steps and may consume credits even if
            it is stopped, fails to produce the result you wanted, or requires revisions. Paying for processing does
            not guarantee a particular result. Express spending limits, required approvals, billing corrections, and
            mandatory refund rights remain applicable.
          </Paragraph>
        </Section>

        <Section title="QUICK TOP-UP">
          <Paragraph>
            Where available, Quick Top-Up lets you make a one-time credit purchase using a saved payment method. You
            must confirm the displayed pack and price before the payment is submitted. Saving a payment method or
            confirming a one-time purchase does not authorize future automatic purchases. A separate Auto Top-Up opt-in may be offered during checkout.
          </Paragraph>
        </Section>

        <Section title="AUTO TOP-UP AUTHORIZATION">
          <Paragraph>
            Auto Top-Up is optional and requires active Plus access and an eligible saved payment method. When you
            affirmatively enable it, you authorize GAB AI INC and its payment processor to charge the selected
            payment method for the credit pack, price, currency, balance threshold, and monthly spending limit you
            review and approve. This is an authorization for recurring purchases triggered by usage, not a fixed
            monthly subscription charge. Each qualifying purchase can occur without a separate confirmation until you
            disable the feature, subject to the limits below.
          </Paragraph>
          <Paragraph>
            Using Work, scheduling a task, buying credits once, or accepting these Terms does not by itself enable
            Auto Top-Up. If you have separately enabled Auto Top-Up, usage across your account, including authorized
            Work and scheduled or background tasks, may reduce your available credits and trigger automatic purchases
            while you are not viewing the Service.
          </Paragraph>
        </Section>

        <Section title="THRESHOLDS AND SPENDING LIMITS">
          <Paragraph>
            You select a balance threshold of 100, 250, 500, or 1,000 credits and an eligible credit pack larger than
            that threshold. An automatic purchase is eligible when your verified available balance is below the
            threshold. The threshold is a trigger for a purchase, not a guaranteed minimum balance.
          </Paragraph>
          <Paragraph>
            You may select a monthly automatic-purchase limit from $1 to $3,000, provided it covers at least one
            selected pack, or expressly choose no monthly limit. No monthly limit means there is no monthly dollar
            cap on these automatic purchases; the 24-hour restriction still applies. The monthly period follows the
            calendar month in UTC. Completed purchases and pending payment reservations count toward the limit;
            unresolved payments remain reserved until their outcome is determined. A purchase is not started if the
            full pack would exceed the remaining automatic-purchase budget.
          </Paragraph>
          <Paragraph>
            This limit applies only to Auto Top-Up purchases. It does not limit credit consumption, one-time
            purchases, subscription fees, or charges from third-party services, and is not a spending limit for an
            individual Work task. Review task budgets, schedules, and other billing settings separately.
          </Paragraph>
        </Section>

        <Section title="TIMING, PAYMENT STATUS, AND RECEIPTS">
          <Paragraph>
            Auto Top-Up attempts are limited to at most one per 24-hour period. Newly saved payment methods have a
            24-hour waiting period. Balance checks are periodic, generally about every 10 minutes, and processing can
            take longer during delays or while usage is settling. Auto Top-Up does not guarantee uninterrupted access
            or task completion; Work may pause or stop before credits are added.
          </Paragraph>
          <Paragraph>
            If a payment outcome is uncertain, additional automatic purchases are blocked while that payment is
            checked. A successful payment may appear before the corresponding credits have finished posting. We send
            an email receipt for completed Auto Top-Up purchases; payment and email delivery may be delayed. Contact
            support if you see a charge without the corresponding credits.
          </Paragraph>
        </Section>

        <Section title="CHANGES AND DISABLING AUTO TOP-UP">
          <Paragraph>
            You can review or disable Auto Top-Up in your account settings at any time. Disabling it stops new
            automatic purchases after the setting is saved, but a payment already submitted to the processor may
            still complete and the purchased credits will be added. Disabling Auto Top-Up does not cancel your
            subscription, reverse completed purchases, or stop Work tasks using credits already available.
          </Paragraph>
          <Paragraph>
            Changes to the selected pack, price, credit amount, payment method, threshold, or monthly limit require
            review and renewed authorization before purchases under those changed settings. If an authorized pack or
            payment method changes, automatic purchases pause pending review. A general price-change notice does not
            authorize a different Auto Top-Up charge.
          </Paragraph>
          <Paragraph>
            Auto Top-Up may pause when you are no longer eligible, billing is under review, or a payment requires
            attention. It is disabled after three consecutive declined payments. Canceling Plus through Gab AI
            disables Auto Top-Up; automatic purchases also require current Plus access. If your subscription is
            managed by an external provider, you can disable Auto Top-Up directly in Gab AI settings without waiting
            for the provider to report a cancellation.
          </Paragraph>
        </Section>

        <Section title="PAYMENT INFORMATION AND BILLING ERRORS">
          <Paragraph>
            Payment processors handle saved payment credentials. Gab AI keeps the payment references, authorization
            settings, and transaction records needed to administer credit purchases. You are responsible for keeping
            your billing details current and reviewing the settings you authorize.
          </Paragraph>
          <Paragraph>
            For a missing credit allocation or a suspected unauthorized, duplicate, or incorrect charge, contact
            ai@gab.com. We will investigate and correct confirmed billing errors, including issuing refunds where
            appropriate or legally required. Nothing in these Terms requires you to waive a lawful payment dispute,
            chargeback, cancellation, or refund right.
          </Paragraph>
        </Section>
      </Section>

      <Section title="4. Accessing Paid Services">
        <Paragraph>
          Certain types of Paid Services may require you to use a device that meets the system and compatibility requirements for such Paid Service, which may change from time to time. You shall have sole responsibility for obtaining a device which satisfies those requirements.
        </Paragraph>
      </Section>

      <Section title="5. License">
        <Paragraph>
          Subject to these Terms, the Terms of Service, and any restrictions expressly disclosed for a particular feature or third-party material, you may use the Paid Services for personal or business purposes and may use your Output commercially. You receive a non-exclusive right to access and use the Paid Services. This permission does not transfer ownership of the Service or authorize resale, sublicensing, or sharing access to the Service. Rights in Output and third-party materials are governed by the Terms of Service and applicable third-party licenses.
        </Paragraph>
      </Section>

      <Section title="6. Restrictions">
        <Paragraph>
          When you use the Paid Service, you may not (or attempt to):
        </Paragraph>
        <List items={[
          'violate the Terms of Service;',
          'use the Paid Service in an illegal manner or for an illegal purpose;',
          'share your account password with someone else to allow them to access any Paid Service that such person did not order;',
          'copy, sell, rent, or sublicense the Paid Services to any third party;',
          'circumvent, reverse-engineer, modify, disable, or otherwise tamper with any security technology that Gab uses to protect the Paid Service or encourage or help anyone else to do so;',
          'access the Paid Service other than by means authorized by Gab; or',
          'remove any proprietary notices or labels on Paid Services.'
        ]} />
      </Section>

      <Section title="7. Changes">
        <Paragraph>
          Gab reserves the right to change the availability and content of the Paid Services at any time with or without notice to you. In addition, we reserve the right to modify, suspend, or discontinue any Paid Service with or without notice to you and we will not be liable to you or any third party for any such modifications, suspension, or termination. However, this will not affect your ability to request a refund as described above.
        </Paragraph>
        <Paragraph>
          In certain cases, content available within a Paid Service may become unavailable. Gab will have no liability to you for any such unavailability. We may also change these Paid Service Terms from time to time so we encourage you to periodically review the most up-to-date version.
        </Paragraph>
        <Paragraph>
          We will provide notice of material changes and any advance notice or renewed acceptance required by law. Changes apply from their stated effective date and do not eliminate rights relating to earlier transactions. Continued use does not replace separate consent required for Auto Top-Up purchases or changed billing authorizations. If you do not accept revised terms, you may stop using the affected Paid Services and disable future automatic purchases.
        </Paragraph>
      </Section>

      <Section title="8. Communications">
        <Paragraph>
          By using the Paid Services, you consent to receiving communications from us including marketing communications such as newsletters about Gab features and content, special offers, promotional announcements, and customer surveys, to your registered email address or via other methods.
        </Paragraph>
      </Section>

      <Section title="9. Other Terms">
        <Paragraph>
          You acknowledge and agree that certain content available in the Paid Services may be considered offensive to some people and that such content may not be labeled as such. Additionally, certain descriptions of Paid Services or content available in the Paid Services are not guaranteed to be accurate. You agree to use the Paid Services at your own risk and, subject to applicable laws, Gab will have no liability to you for any content that you find offensive or inaccurate.
        </Paragraph>
        <Paragraph>
          The Paid Services are being provided by Gab AI Inc, reachable at:
        </Paragraph>
        <Paragraph>
          <Text weight='medium'>Gab AI Inc.</Text>{'\n'}
          1143 Northern Blvd #158{'\n'}
          Clarks Summit, PA 18411{'\n'}
          United States of America
        </Paragraph>
      </Section>

      <Section title="Contact Information">
        <Paragraph>
          For questions, please contact us at:
        </Paragraph>
        <Paragraph>
          <Text weight='medium'>Gab AI Inc.</Text>{'\n'}
          1143 Northern Blvd #158{'\n'}
          Clarks Summit, PA 18411{'\n'}
          United States of America
        </Paragraph>
        <Paragraph>
          Email: ai@gab.com
        </Paragraph>
      </Section>
    </LegalPage>
  );
};

export default React.memo(TermsOfSalePage);
